import {
  ApplicationConfig,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  isDevMode,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import { provideRouter } from '@angular/router';
import { providePrimeNG } from 'primeng/config';

import { routes } from './app.routes';

// Registrar los datos de locale es-AR globalmente en el bootstrap, para que DecimalPipe/
// DatePipe/CurrencyPipe funcionen en cualquier ruta sin depender de que otro componente
// (p. ej. la tabla) lo haya registrado antes como efecto secundario.
registerLocaleData(localeEsAr);
import { provideServiceWorker } from '@angular/service-worker';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import AppPreset from './shared/primeng/preset';
import { ConfigService, ConfigClientService } from './core/services';
import { AuthService } from './core/services/auth.service';
import { ConfigGeneralService } from './core/services/config-general/config-general.service';

// Note: @angular/cdk Overlay is providedIn: 'root' since CDK v14+,
// so no extra module import is needed here.
export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    { provide: LOCALE_ID, useValue: 'es-AR' },
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideAppInitializer(async () => {
      // inject() solo es válido antes del primer `await` (contexto de inyección síncrono):
      // todas las resoluciones de servicios van primero, nada de inject() más abajo.
      const configService = inject(ConfigService);
      const configClientService = inject(ConfigClientService);
      const authService = inject(AuthService);
      const configGeneralService = inject(ConfigGeneralService);

      try {
        // Static config is loaded from build-time environment files.
        const apiConfigUrl = configService.getAppConfig()?.api_config;
        await configClientService.loadConfig();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Could not load client config', error);
      }

      // Si hay una sesión persistida (refresh / reapertura de la app), recarga ConfGral
      // antes de renderizar — igual que el legacy (ver .ia/.amigrar/confGral.md, punto 2.1).
      // `cargarConfiguracionConAuth()` nunca rechaza, así que este await no puede colgar
      // el splash "Cargando aplicación…" ante un fallo de red.
      if (authService.isLoggedIn()) {
        await configGeneralService.cargarConfiguracionConAuth();
      }
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    providePrimeNG({
      theme: {
        preset: AppPreset,
        options: {
          // Syncs PrimeNG dark mode with our [data-theme="dark"] attribute
          darkModeSelector: '[data-theme="dark"]',
          // Avoid CSS layer conflicts with our own SCSS architecture
          cssLayer: false,
        },
      },
    }),
  ],
};
